import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import { darken, lighten, useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import { toast } from 'react-toastify';
import SectionLoading from '@components/SectionLoading';
import PieWrapper from '@containers/Admin/containers/ActivityPage/__fixtures__/__ActivityPage/containers/ActivityStats/components/PieWrapper';
import { ROLES } from '@shared/constants/roles';
import { getAnalytics, getAnalyticsBusinessUnits } from './services';
import {
  Page,
  Header,
  Title,
  FilterPanel,
  FilterLabel,
  FilterControls,
  ModeRow,
  ModeButton,
  UnitPicker,
  UnitSelectButton,
  UnitMenu,
  UnitSearch,
  UnitOptions,
  UnitOption,
  UnitName,
  TestBadge,
  UnitMenuFooter,
  UnitMenuActions,
  UnitMenuAction,
  UnitMenuDone,
  Layout,
  Tabs,
  Tab,
  Content,
  SectionTitle,
  MetricGrid,
  PatientsMetricGrid,
  Card,
  CardHeader,
  CardLabel,
  CardPeriod,
  CardValue,
  CardHelp,
  ValueSuffix,
  DetailSubtitle,
  DetailGrid,
  DetailCard,
  BarRow,
  BarTrack,
  BarFill,
  Percentage,
  Count,
  PeriodControl,
  CustomDates,
  CourseCard,
  CourseBody,
  CoursePie,
  CourseLegend,
  CourseLegendRow,
  CourseSwatch,
  CourseLabel,
  CourseName,
  CourseCount,
} from './styles';

const TABS = [
  { label: 'Overview', Icon: DashboardOutlinedIcon },
  { label: 'Patients', Icon: PeopleOutlineIcon },
  { label: 'Practice', Icon: MicNoneOutlinedIcon },
  { label: 'Feedback', Icon: FactCheckOutlinedIcon },
  { label: 'Treatment goals', Icon: TrackChangesOutlinedIcon },
];
const PERIODS = [
  ['SEVEN_DAYS', '7 days'],
  ['THIRTY_DAYS', '30 days'],
  ['NINETY_DAYS', '90 days'],
  ['TWELVE_MONTHS', '12 months'],
  ['ALL_TIME', 'All time'],
  ['CUSTOM', 'Custom'],
];

const ROLE_LABELS = {
  PATIENT: 'Patients',
  THERAPIST: 'Therapists',
  SUPERVISOR: 'Clinical Supervisors',
  MANAGEMENT_ADMIN: 'Administrative Managers',
  TECH_ADMIN: 'Super Admins',
};

const getPrescriptionLengthLabel = ({ min, max }) => {
  if (min === 0) return `Under ${max} month`;
  if (max === null) return `Over ${min} months`;
  return `${min}-${max} months`;
};

const getAgeGroupLabel = ({ min, max }) => {
  if (min === 0) return `${max} and under`;
  if (max === null) return `${min}+`;
  return `${min}-${max}`;
};

const DEFAULT_RANGE = { period: 'SEVEN_DAYS', startDate: '', endDate: '' };
const PERIOD_DAYS = {
  SEVEN_DAYS: 7,
  THIRTY_DAYS: 30,
  NINETY_DAYS: 90,
  TWELVE_MONTHS: 365,
};

const toAnalyticsDateRange = ({ period, startDate, endDate }) => {
  if (period === 'ALL_TIME') return null;
  if (period === 'CUSTOM') {
    if (!startDate || !endDate) return null;
    return {
      from: new Date(`${startDate}T00:00:00.000Z`).toISOString(),
      to: new Date(`${endDate}T23:59:59.999Z`).toISOString(),
    };
  }

  const to = new Date();
  const from = new Date(to.getTime() - PERIOD_DAYS[period] * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to: to.toISOString() };
};

const formatValue = (value, decimals) => {
  if (value === null || value === undefined) return '-';
  return Number(value).toLocaleString(
    undefined,
    decimals === undefined
      ? {}
      : {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        },
  );
};

const PeriodSelect = ({ value, onChange }) => (
  <PeriodControl>
    <Select
      value={value.period}
      onChange={(event) => onChange({ ...value, period: event.target.value })}
      size="small"
      variant="standard"
    >
      {PERIODS.map(([periodValue, label]) => (
        <MenuItem key={periodValue} value={periodValue}>
          {label}
        </MenuItem>
      ))}
    </Select>
    {value.period === 'CUSTOM' ? (
      <CustomDates>
        <TextField
          type="date"
          size="small"
          value={value.startDate}
          onChange={(event) => onChange({ ...value, startDate: event.target.value })}
          inputProps={{ 'aria-label': 'Start date' }}
        />
        <TextField
          type="date"
          size="small"
          value={value.endDate}
          onChange={(event) => onChange({ ...value, endDate: event.target.value })}
          inputProps={{ 'aria-label': 'End date' }}
        />
      </CustomDates>
    ) : null}
  </PeriodControl>
);

const MetricCard = ({
  label,
  value,
  help,
  period = 'Now',
  decimals,
  onPeriodChange,
  compact,
  patient,
  therapist,
  valueSuffix,
}) => (
  <Card compact={compact} patient={patient} therapist={therapist}>
    <CardHeader>
      <CardLabel>{label}</CardLabel>
      {onPeriodChange ? <PeriodSelect value={period} onChange={onPeriodChange} /> : <CardPeriod>{period}</CardPeriod>}
    </CardHeader>
    <CardValue>
      {formatValue(value, decimals)}
      {valueSuffix ? <ValueSuffix>{valueSuffix}</ValueSuffix> : null}
    </CardValue>
    <CardHelp>{help}</CardHelp>
  </Card>
);

const BreakdownCard = ({
  title,
  subtitle,
  items = [],
  labels = {},
  getLabel,
  fullWidth,
  roleCard,
  patientBreakdown,
}) => {
  const total = items.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
  return (
    <DetailCard fullWidth={fullWidth} roleCard={roleCard} patientBreakdown={patientBreakdown}>
      <CardHeader>
        <CardLabel>{title}</CardLabel>
        <CardPeriod>Now</CardPeriod>
      </CardHeader>
      {subtitle ? <DetailSubtitle>{subtitle}</DetailSubtitle> : null}
      {items.map((item) => {
        const percent = total ? Math.round(((Number(item.count) || 0) * 1000) / total) / 10 : 0;
        const itemKey = item.key || item.role || `${item.min}-${item.max ?? 'UP'}`;
        return (
          <BarRow key={itemKey}>
            <span>{getLabel ? getLabel(item) : labels[itemKey] || itemKey}</span>
            <BarTrack>
              <BarFill width={percent} />
            </BarTrack>
            <Percentage>{percent}%</Percentage>
            <Count>{formatValue(item.count)}</Count>
          </BarRow>
        );
      })}
      {!items.length ? <CardHelp>No data available</CardHelp> : null}
    </DetailCard>
  );
};

const createCoursePalette = (baseColors, colorCount) => {
  const mutationStepCount = Math.max(Math.ceil((Math.ceil(colorCount / baseColors.length) - 1) / 2), 1);

  return Array.from({ length: colorCount }, (_, index) => {
    const baseIndex = index % baseColors.length;
    const baseColor = baseColors[baseIndex];
    const mutationLevel = Math.floor(index / baseColors.length);
    if (!mutationLevel) return baseColor;

    const mutationStep = Math.ceil(mutationLevel / 2);
    const mutationAmount = 0.3 + 0.32 * ((mutationStep - 1) / mutationStepCount);
    if (mutationLevel % 2) {
      const lightAmount = Math.min(mutationAmount + (baseIndex === 2 ? 0.15 : 0), 0.75);
      return lighten(baseColor, lightAmount);
    }

    const darkAmount = Math.min(mutationAmount + 0.2 + baseIndex * 0.12, 0.8);
    return darken(baseColor, darkAmount);
  });
};

const CoursesCard = ({ items = [], registeredPatients }) => {
  const theme = useTheme();
  const baseCourseColors = [
    theme.palette.novotalk.$activeColor,
    theme.palette.novotalk.$navigationHelpIconColor,
    lighten(theme.palette.novotalk.$activeColor, 0.55),
    theme.palette.novotalk.$textColorDark,
  ];
  const courseColors = createCoursePalette(baseCourseColors, items.length);
  const total = items.reduce((sum, item) => sum + (Number(item.count) || 0), 0);
  const chartData = items.reduce((data, item) => ({ ...data, [item.key]: Number(item.count) || 0 }), {});

  return (
    <CourseCard patientBreakdown>
      <CardHeader>
        <CardLabel>Courses</CardLabel>
        <CardPeriod>Now</CardPeriod>
      </CardHeader>
      <DetailSubtitle>Share of {formatValue(registeredPatients)} active patients by course</DetailSubtitle>
      {items.length ? (
        <CourseBody>
          <CoursePie>
            <PieWrapper data={chartData} colors={courseColors} width={150} height={150} />
          </CoursePie>
          <CourseLegend>
            {items.map((item, index) => {
              const percent = total ? Math.round(((Number(item.count) || 0) * 1000) / total) / 10 : 0;
              return (
                <CourseLegendRow key={item.key}>
                  <CourseSwatch chartColor={courseColors[index]} />
                  <CourseLabel>
                    <CourseName title={item.key}>{item.key}</CourseName>
                    <CourseCount>{formatValue(item.count)}</CourseCount>
                  </CourseLabel>
                  <Percentage>{percent}%</Percentage>
                </CourseLegendRow>
              );
            })}
          </CourseLegend>
        </CourseBody>
      ) : (
        <CardHelp>No data available</CardHelp>
      )}
    </CourseCard>
  );
};

const Analytics = () => {
  const { roles = [] } = useSelector((state) => state.getIn(['app', 'authentication', 'user']).toJS());
  const canSelectBusinessUnits = roles.includes(ROLES.TECH_ADMIN);
  const [activeTab, setActiveTab] = useState('Overview');
  const [mode, setMode] = useState('all');
  const [businessUnits, setBusinessUnits] = useState([]);
  const [selectedBusinessUnits, setSelectedBusinessUnits] = useState([]);
  const [unitMenuOpen, setUnitMenuOpen] = useState(false);
  const [businessUnitSearch, setBusinessUnitSearch] = useState('');
  const [activePatientsRange, setActivePatientsRange] = useState(DEFAULT_RANGE);
  const [patientsStartedRange, setPatientsStartedRange] = useState(DEFAULT_RANGE);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!canSelectBusinessUnits) return;

    getAnalyticsBusinessUnits().then((result) => {
      setBusinessUnits(result?.data?.allBusinessUnits || []);
    });
  }, [canSelectBusinessUnits]);

  const analyticsVariables = useMemo(
    () => ({
      ...(canSelectBusinessUnits && mode === 'selected' ? { businessUnitIds: selectedBusinessUnits } : {}),
      activePatientsRange: toAnalyticsDateRange(activePatientsRange),
      patientsStartedRange: toAnalyticsDateRange(patientsStartedRange),
    }),
    [activePatientsRange, canSelectBusinessUnits, mode, patientsStartedRange, selectedBusinessUnits],
  );

  const loadAnalytics = useCallback(async () => {
    try {
      const hasIncompleteCustomRange = [activePatientsRange, patientsStartedRange].some(
        (range) => range.period === 'CUSTOM' && (!range.startDate || !range.endDate),
      );
      if (
        (canSelectBusinessUnits && mode === 'selected' && !selectedBusinessUnits.length) ||
        hasIncompleteCustomRange
      ) {
        setAnalytics(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      const result = await getAnalytics(analyticsVariables);
      setAnalytics({
        overview: result.analyticsOverview,
        patients: result.analyticsPatients,
        practice: result.analyticsPractice,
        feedback: result.analyticsFeedback,
        treatmentGoals: result.analyticsTreatmentGoals,
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setAnalytics(null);
      console.error(error);
      toast.error('Failed to load analytics data');
    }
  }, [
    activePatientsRange,
    analyticsVariables,
    canSelectBusinessUnits,
    mode,
    patientsStartedRange,
    selectedBusinessUnits.length,
  ]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const overview = analytics?.overview || {};
  const patients = analytics?.patients || {};
  const filteredBusinessUnits = businessUnits.filter((businessUnit) =>
    businessUnit.name.toLowerCase().includes(businessUnitSearch.trim().toLowerCase()),
  );

  const toggleBusinessUnit = (businessUnitId) => {
    setSelectedBusinessUnits((selected) =>
      selected.includes(businessUnitId)
        ? selected.filter((id) => id !== businessUnitId)
        : [...selected, businessUnitId],
    );
  };

  const renderOverview = () => (
    <>
      <MetricGrid>
        <MetricCard
          label="Registered users"
          value={overview.registeredUsers}
          help="All users registered in the system"
        />
        <MetricCard
          label="All time users"
          value={overview.allTimeUsers}
          help="Everyone who ever registered"
          period="All time"
        />
      </MetricGrid>
      <BreakdownCard roleCard title="Users by role" items={overview.usersByRole} labels={ROLE_LABELS} />
    </>
  );

  const renderPatients = () => (
    <>
      <PatientsMetricGrid>
        <MetricCard
          patient
          label="All time patients"
          value={patients.allTimePatients}
          help="All patients in every state"
          period="All time"
        />
        <MetricCard
          patient
          label="Registered patients"
          value={patients.registeredPatients}
          help="Patients in active state"
        />
        <MetricCard
          patient
          label="Active patients"
          value={patients.activePatients}
          help="Patients actively practicing"
          period={activePatientsRange}
          onPeriodChange={setActivePatientsRange}
        />
        <MetricCard
          patient
          label="Patients started"
          value={patients.patientsStarted}
          help="Patients who began a course"
          period={patientsStartedRange}
          onPeriodChange={setPatientsStartedRange}
        />
        <MetricCard
          therapist
          label="Patients per therapist"
          value={patients.patientsPerTherapist}
          decimals={1}
          valueSuffix=" on average"
          help="Active patients assigned to each active therapist"
      />
      </PatientsMetricGrid>
      <DetailGrid>
        <CoursesCard items={patients.courses} registeredPatients={patients.registeredPatients} />
        <BreakdownCard
          patientBreakdown
          title="Age groups"
          subtitle={`Share of ${formatValue(patients.registeredPatients)} active patients by age`}
          items={patients.ageGroups}
          getLabel={getAgeGroupLabel}
        />
      </DetailGrid>
      <BreakdownCard
        fullWidth
        title="Prescription length"
        subtitle="Time since active patients started"
        items={patients.prescriptionLength}
        getLabel={getPrescriptionLengthLabel}
      />
    </>
  );

  return (
    <Page>
      <Header>
        <Title>Analytics</Title>
        {!!canSelectBusinessUnits && (
          <FilterPanel>
            <FilterLabel>Business Units:</FilterLabel>
            <FilterControls>
              <ModeRow>
                <ModeButton active={mode === 'all'} onClick={() => setMode('all')}>
                  Entire system
                </ModeButton>
                <ModeButton
                  active={mode === 'selected'}
                  onClick={() => {
                    setMode('selected');
                    if (!selectedBusinessUnits.length) {
                      setSelectedBusinessUnits(
                        businessUnits
                          .filter((businessUnit) => !businessUnit.isTest)
                          .map((businessUnit) => businessUnit.id),
                      );
                    }
                  }}
                >
                  Selected units
                </ModeButton>
              </ModeRow>
              {mode === 'selected' ? (
                <ClickAwayListener onClickAway={() => setUnitMenuOpen(false)}>
                  <UnitPicker>
                    <UnitSelectButton
                      type="button"
                      aria-haspopup="dialog"
                      aria-expanded={unitMenuOpen}
                      onClick={() => setUnitMenuOpen((open) => !open)}
                    >
                      <span>
                        {selectedBusinessUnits.length
                          ? `${selectedBusinessUnits.length} of ${businessUnits.length} units selected`
                          : 'Choose business units'}
                      </span>
                      <ExpandMoreIcon />
                    </UnitSelectButton>
                    {unitMenuOpen ? (
                      <UnitMenu role="dialog" aria-label="Choose business units">
                        <UnitSearch>
                          <SearchIcon />
                          <input
                            type="search"
                            aria-label="Find a business unit"
                            placeholder="Find a business unit"
                            value={businessUnitSearch}
                            onChange={(event) => setBusinessUnitSearch(event.target.value)}
                          />
                        </UnitSearch>
                        <UnitOptions>
                          {filteredBusinessUnits.map((businessUnit) => (
                            <UnitOption key={businessUnit.id}>
                              <Checkbox
                                checked={selectedBusinessUnits.includes(businessUnit.id)}
                                onChange={() => toggleBusinessUnit(businessUnit.id)}
                                size="small"
                              />
                              <UnitName>{businessUnit.name}</UnitName>
                              {businessUnit.isTest ? <TestBadge>Test</TestBadge> : null}
                            </UnitOption>
                          ))}
                        </UnitOptions>
                        <UnitMenuFooter>
                          <UnitMenuActions>
                            <UnitMenuAction
                              type="button"
                              onClick={() => setSelectedBusinessUnits(businessUnits.map(({ id }) => id))}
                            >
                              Select All
                            </UnitMenuAction>
                            <UnitMenuAction type="button" onClick={() => setSelectedBusinessUnits([])}>
                              Clear All
                            </UnitMenuAction>
                            <UnitMenuAction
                              type="button"
                              onClick={() =>
                                setSelectedBusinessUnits(
                                  businessUnits.filter((businessUnit) => !businessUnit.isTest).map(({ id }) => id),
                                )
                              }
                            >
                              Exclude Test Units
                            </UnitMenuAction>
                          </UnitMenuActions>
                          <UnitMenuDone type="button" onClick={() => setUnitMenuOpen(false)}>
                            Done
                          </UnitMenuDone>
                        </UnitMenuFooter>
                      </UnitMenu>
                    ) : null}
                  </UnitPicker>
                </ClickAwayListener>
              ) : null}
            </FilterControls>
          </FilterPanel>
        )}
      </Header>
      <Layout>
        <Tabs aria-label="Analytics sections">
          {TABS.map(({ label, Icon }) => (
            <Tab key={label} active={activeTab === label} onClick={() => setActiveTab(label)}>
              <Icon />
              <span>{label}</span>
            </Tab>
          ))}
        </Tabs>
        <Content>
          <SectionTitle>{activeTab}</SectionTitle>
          {loading ? <SectionLoading /> : null}
          {!loading && activeTab === 'Overview' ? renderOverview() : null}
          {!loading && activeTab === 'Patients' ? renderPatients() : null}
        </Content>
      </Layout>
    </Page>
  );
};

export default Analytics;
